import { useState, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { useQueryClient } from "@tanstack/react-query";
import { Power, ArrowRight, ScanLine, Loader2, CheckCircle2, PauseCircle, Info } from "lucide-react";
import BarcodeScanner from "@/components/BarcodeScanner";
import CoilForm from "@/components/CoilForm";
import { WeighPanel } from "@/components/inward/WeighPanel";
import { BundleWeighPanel } from "@/components/inward/BundleWeighPanel";
import { createCoil, getInwardInfo, bridgeDownloadUrl } from "@/lib/api";

const beep = () => {
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return;
    const ctx = new Ctx();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.frequency.value = 880;
    gain.gain.setValueAtTime(0.15, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.18);
    osc.start();
    osc.stop(ctx.currentTime + 0.2);
  } catch { /* audio not available */ }
};

const STATUS_TEXT = {
  received: ["Already received", "bg-green-50 text-green-800", CheckCircle2],
  received_hold: ["On Received – Hold — a Director must release it in Inventory", "bg-red-50 text-red-800", PauseCircle],
};

const Scan = () => {
  const [scannerActive, setScannerActive] = useState(true);
  const [manualId, setManualId] = useState("");
  const [loading, setLoading] = useState(false);
  const [view, setView] = useState({ mode: "idle" }); // idle | weigh | known | new
  const [done, setDone] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const lastScanRef = useRef({ value: "", time: 0 });
  const qc = useQueryClient();

  const reset = () => { setView({ mode: "idle" }); setDone(false); setScannerActive(true); };

  const lookup = useCallback(async (raw) => {
    const id = raw.trim();
    if (!id) return;
    setLoading(true);
    setDone(false);
    try {
      const info = await getInwardInfo(id);
      if (info.coil.status === "pending") setView({ mode: "weigh", info });
      else setView({ mode: "known", coil: info.coil });
    } catch (e) {
      if (e?.response?.status === 404) setView({ mode: "new", barcode: id });
      else toast.error(e?.response?.data?.detail || "Lookup failed");
    } finally {
      setLoading(false);
    }
  }, []);

  const handleDetected = useCallback(async (text) => {
    const now = Date.now();
    if (lastScanRef.current.value === text && now - lastScanRef.current.time < 1500) return;
    lastScanRef.current = { value: text, time: now };
    setScannerActive(false);
    beep();
    await lookup(text);
  }, [lookup]);

  const submitManual = (e) => { e.preventDefault(); setScannerActive(false); lookup(manualId); setManualId(""); };

  const handleCreate = async (payload, resetForm) => {
    setSubmitting(true);
    try {
      await createCoil(payload);
      toast.success(`Coil ${payload.coil_id} added to inventory`);
      resetForm();
      qc.invalidateQueries({ queryKey: ["coils"] });
      reset();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to save coil");
    } finally {
      setSubmitting(false);
    }
  };

  const onWeighDone = () => { setDone(true); qc.invalidateQueries({ queryKey: ["coils"] }); qc.invalidateQueries({ queryKey: ["stats"] }); };

  return (
    <div data-testid="scan-page" className="space-y-4">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <p className="text-sm font-medium text-slate-500">Intake</p>
          <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900">RM / SFG Inward</h2>
        </div>
        <div className="flex items-center gap-2">
          <a href={bridgeDownloadUrl()} data-testid="bridge-kit-download" title="Setup kit for the PC that reads the crane scale (also prints labels)"
            className="inline-flex items-center gap-1.5 text-sm font-medium text-slate-500 hover:text-slate-900 px-3 h-11 transition-colors">
            Scale bridge kit
          </a>
          <Link to="/inventory?kind=mother" data-testid="view-rm-in-inventory"
            className="inline-flex items-center gap-1.5 text-sm font-medium text-slate-700 hover:text-slate-900 bg-white border border-slate-200 rounded-full px-4 h-11 transition-colors">
            Inventory <ArrowRight className="h-4 w-4" />
          </Link>
          <button data-testid="toggle-scanner-btn" onClick={() => setScannerActive((s) => !s)}
            className="inline-flex items-center gap-1.5 text-sm font-medium bg-white border border-slate-200 rounded-full px-4 h-11 hover:bg-slate-50 transition-colors">
            <Power className="h-4 w-4" /> {scannerActive ? "Stop camera" : "Start camera"}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
        <div className="lg:col-span-5 xl:col-span-4 space-y-4 lg:sticky lg:top-4">
          <div className="bg-white rounded-3xl border border-slate-200 overflow-hidden">
            <BarcodeScanner active={scannerActive} onDetected={handleDetected} />
          </div>
          <form onSubmit={submitManual} className="bg-white rounded-3xl border border-slate-200 p-5 space-y-3">
            <label htmlFor="manual-id" className="text-base font-semibold text-slate-900">Or type the RM / SFG ID</label>
            <input id="manual-id" data-testid="manual-lookup-input" value={manualId} onChange={(e) => setManualId(e.target.value.toUpperCase())}
              placeholder="e.g. RM-1234 or SFG560" autoCapitalize="characters" autoComplete="off"
              className="w-full h-16 px-5 rounded-2xl border-2 border-slate-300 focus:border-slate-900 outline-none text-2xl font-mono-data tracking-wide transition-colors" />
            <button type="submit" data-testid="manual-lookup-btn" disabled={!manualId.trim() || loading}
              className="w-full h-14 rounded-2xl bg-slate-900 text-white text-lg font-semibold hover:bg-slate-800 disabled:opacity-40 transition-colors inline-flex items-center justify-center gap-2">
              {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : <ScanLine className="h-5 w-5" />} Load details
            </button>
          </form>
        </div>

        <div className="lg:col-span-7 xl:col-span-8 space-y-4">
          {view.mode === "idle" && (
            <div data-testid="inward-idle" className="rounded-3xl border-2 border-dashed border-slate-200 bg-white/60 min-h-[420px] flex flex-col items-center justify-center text-center p-10">
              <ScanLine className="h-14 w-14 text-slate-300" />
              <p className="mt-4 text-2xl font-semibold text-slate-700">Scan a coil tag to begin</p>
              <p className="mt-1 text-slate-500">Or type its RM / SFG ID on the left.</p>
            </div>
          )}

          {view.mode === "weigh" && (
            <WeighPanel key={view.info.coil.coil_id} info={view.info} onDone={onWeighDone} onCancel={reset}
              onBundle={() => setView({ mode: "bundle", coilId: view.info.coil.coil_id })} />
          )}

          {view.mode === "bundle" && (
            <BundleWeighPanel key={`b-${view.coilId}`} startCoilId={view.coilId} onDone={onWeighDone} onCancel={reset} />
          )}

          {view.mode === "known" && (() => {
            const [text, cls, Icon] = STATUS_TEXT[view.coil.status] || [`Status: ${(view.coil.status || "").replace(/_/g, " ")}`, "bg-slate-100 text-slate-700", Info];
            return (
              <div data-testid="inward-known" className={`rounded-3xl p-8 ${cls}`}>
                <p className="text-sm font-medium opacity-80">{view.coil.kind === "sfg" ? "SFG" : "RM"}</p>
                <p className="text-4xl font-bold font-mono-data mt-1">{view.coil.coil_id}</p>
                <p data-testid="inward-known-status" className="mt-4 text-xl font-semibold flex items-center gap-2"><Icon className="h-6 w-6" /> {text}</p>
              </div>
            );
          })()}

          {view.mode === "new" && (
            <div data-testid="inward-new" className="bg-white rounded-3xl border border-slate-200 p-6 lg:p-8">
              <p className="text-sm text-slate-500">Unknown ID — not on any packaging list</p>
              <h3 className="text-2xl font-bold text-slate-900 mt-1 mb-5">Add this coil</h3>
              <CoilForm initialBarcode={view.barcode} onSubmit={handleCreate} submitting={submitting} />
            </div>
          )}

          {(done || view.mode === "known") && (
            <button data-testid="scan-next-btn" onClick={reset}
              className="w-full h-16 rounded-2xl bg-white border-2 border-slate-900 text-slate-900 text-lg font-semibold hover:bg-slate-50 transition-colors">
              Scan next coil
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default Scan;
